import moment from 'moment';
import { useCallback } from 'react';
import {
  ALLOCATION_PANEL_AS_OF_DATE_DISPLAY_FORMAT,
  UNSAFE_deserializeAsOfDateSetting,
  serializeAsOfDateSetting,
} from 'venn-utils';
import type { AsOfDateSetting } from 'venn-utils';
import { Match } from 'effect';
import { formatDateForCalendarDropdown } from './calendarDropdownUtils';

type UseCalendarDropdownProps = {
  value: AsOfDateSetting;
  lastUpdated: Date | undefined;
  currentDate: Date | undefined;
  start: Date | undefined;
  end: Date | undefined;
  onChange: (value: AsOfDateSetting) => void;
};

export const useCalendarDropdown = ({
  value,
  lastUpdated,
  currentDate,
  start,
  end,
  onChange,
}: UseCalendarDropdownProps) => {
  const serializedValue = serializeAsOfDateSetting(value);

  const onValueChange = useCallback(
    (value: string) => {
      if (onChange) {
        onChange(UNSAFE_deserializeAsOfDateSetting(value));
      }
    },
    [onChange],
  );

  const begin = moment.utc(start).startOf('month');
  const ending = moment.utc(end);
  const lastUpdatedEndOfMonth = moment.utc(lastUpdated).endOf('month');
  const lastUpdatedValue = !lastUpdatedEndOfMonth.isAfter(end) ? lastUpdatedEndOfMonth : moment.utc(lastUpdated);
  const formattedDisplayDate = Match.value(UNSAFE_deserializeAsOfDateSetting(serializedValue)).pipe(
    Match.tag('Current', () => moment.utc(currentDate).format(ALLOCATION_PANEL_AS_OF_DATE_DISPLAY_FORMAT)),
    Match.tag('LastUploaded', () => lastUpdatedValue.format(ALLOCATION_PANEL_AS_OF_DATE_DISPLAY_FORMAT)),
    Match.tag('Date', ({ date }) => formatDateForCalendarDropdown(date)),
    Match.exhaustive,
  );
  return {
    selectedDate: serializedValue,
    onValueChange,
    begin,
    ending,
    formattedDisplayDate,
    lastUpdatedValue,
  };
};
